import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHero } from '@/components/shared/PageHero';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PROJECTS } from '@/data/mockData';
import { formatDate } from '@/lib/storage';
import { Calendar, MapPin, Users, Search, Hammer } from 'lucide-react';

const STATUSES = ['All', 'Completed', 'Ongoing', 'Planned'];

const Projects = () => {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('All');

  const list = useMemo(() => {
    return PROJECTS.filter((p) => {
      const matchStatus = status === 'All' || p.status === status;
      const matchQ = (p.name + p.description + p.location).toLowerCase().includes(q.toLowerCase());
      return matchStatus && matchQ;
    });
  }, [q, status]);

  const counts = useMemo(() => ({
    Completed: PROJECTS.filter((p) => p.status === 'Completed').length,
    Ongoing: PROJECTS.filter((p) => p.status === 'Ongoing').length,
    Planned: PROJECTS.filter((p) => p.status === 'Planned').length,
  }), []);

  return (
    <div data-testid="projects-page">
      <PageHero
        eyebrow="Projects"
        title="Barangay Projects"
        subtitle="Infrastructure, peace & order, and quality-of-life projects delivered, in progress, and planned."
        breadcrumbs={[{ label: 'Projects' }]}
      />

      <section className="container-app -mt-12 relative z-10 grid grid-cols-3 gap-3 md:gap-4">
        <SummaryCard label="Completed" value={counts.Completed} tone="success" />
        <SummaryCard label="Ongoing" value={counts.Ongoing} tone="accent" />
        <SummaryCard label="Planned" value={counts.Planned} tone="info" />
      </section>

      <section className="container-app section-padding">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="projects-search"
            />
          </div>
          <div className="flex gap-2">
            {STATUSES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={status === s ? 'default' : 'outline'}
                onClick={() => setStatus(s)}
                className={status === s ? 'bg-primary text-primary-foreground' : ''}
                data-testid={`projects-filter-${s.toLowerCase()}`}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>

        {list.length === 0 ? (
          <EmptyState icon={Hammer} title="No projects found" />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((p) => (
              <Card key={p.id} className="overflow-hidden card-elevated border-0" data-testid={`project-${p.id}`}>
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="secondary" className="bg-primary/10 text-primary">{p.category}</Badge>
                    <StatusBadge status={p.status} />
                  </div>
                  <h3 className="mt-3 font-heading font-semibold leading-snug">{p.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                  <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}</div>
                    <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Started {formatDate(p.startDate)}{p.completedDate ? ` · Completed ${formatDate(p.completedDate)}` : p.targetDate ? ` · Target ${formatDate(p.targetDate)}` : ''}</div>
                    <div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{p.beneficiaries}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const SummaryCard = ({ label, value, tone }) => {
  const tones = {
    success: 'from-success to-emerald-400',
    accent: 'from-accent to-cyan-400',
    info: 'from-info to-blue-400',
  };
  return (
    <Card className="p-5 border-0 shadow-md-soft text-center bg-card">
      <div className={`mx-auto h-1.5 w-12 rounded-full bg-gradient-to-r ${tones[tone]} mb-3`} />
      <div className="font-display text-3xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    </Card>
  );
};

export default Projects;
